import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import Layout from './components/Layout';
import { useAuthStore } from './store/auth';

// 按需加载页面，减小首屏bundle体积
const LoginPage = lazy(() => import('./pages/LoginPage/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage/DashboardPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage/OrdersPage'));
const PlayersPage = lazy(() => import('./pages/PlayersPage/PlayersPage'));
const BossesPage = lazy(() => import('./pages/BossesPage/BossesPage'));
const FinancePage = lazy(() => import('./pages/FinancePage/FinancePage'));
const ShopPage = lazy(() => import('./pages/ShopPage/ShopPage'));
const WithdrawalsPage = lazy(() => import('./pages/WithdrawalsPage/WithdrawalsPage'));
const UsersPage = lazy(() => import('./pages/UsersPage/UsersPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage/SettingsPage'));
const InternalPage = lazy(() => import('./pages/InternalPage/InternalPage'));
const CouponsPage = lazy(() => import('./pages/CouponsPage/CouponsPage'));
const LevelsPage = lazy(() => import('./pages/LevelsPage'));
const MyOrdersPage = lazy(() => import('./pages/MyOrdersPage/MyOrdersPage'));
const MyWithdrawalsPage = lazy(() => import('./pages/MyWithdrawalsPage/MyWithdrawalsPage'));
const PlayerRankingPage = lazy(() => import('./pages/PlayerRankingPage/PlayerRankingPage'));
const PlayerStatsPage = lazy(() => import('./pages/PlayerStatsPage/PlayerStatsPage'));
const PlayerAccountPage = lazy(() => import('./pages/PlayerAccountPage/PlayerAccountPage'));
const PlayerFundFlowPage = lazy(() => import('./pages/PlayerFundFlowPage/PlayerFundFlowPage'));
const BossPage = lazy(() => import('./pages/BossPage/BossPage'));
const BossItemPage = lazy(() => import('./pages/BossPage/BossItemPage'));
const BossProfilePage = lazy(() => import('./pages/BossPage/BossProfilePage'));
// 新版老板端（移动端，参照屿禾）
const BossLayout = lazy(() => import('./pages/boss-portal/BossLayout'));
const BossHome = lazy(() => import('./pages/boss-portal/BossHome'));
const BossService = lazy(() => import('./pages/boss-portal/BossService'));
const BossPlayerDetail = lazy(() => import('./pages/boss-portal/BossPlayerDetail'));
const BossMine = lazy(() => import('./pages/boss-portal/BossMine'));
const BossOrders = lazy(() => import('./pages/boss-portal/BossOrders'));
const BossOrderDetail = lazy(() => import('./pages/boss-portal/BossOrderDetail'));
const BossCoupons = lazy(() => import('./pages/boss-portal/BossCoupons'));
const BossAchievements = lazy(() => import('./pages/boss-portal/BossAchievements'));
const BossLottery = lazy(() => import('./pages/boss-portal/BossLottery'));
const BossLotteryRecords = lazy(() => import('./pages/boss-portal/BossLotteryRecords'));
const BossItemDetail = lazy(() => import('./pages/boss-portal/BossItemDetail'));
const BossAfterSale = lazy(() => import('./pages/boss-portal/BossAfterSale'));
const BossRights = lazy(() => import('./pages/boss-portal/BossRights'));
const GameItemsPage = lazy(() => import('./pages/GameItemsPage/GameItemsPage'));
const CustomerServicePage = lazy(() => import('./pages/CustomerServicePage/CustomerServicePage'));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage/ReviewsPage'));
const GiftsPage = lazy(() => import('./pages/GiftsPage/GiftsPage'));
const BigCategoriesPage = lazy(() => import('./pages/BigCategoriesPage/BigCategoriesPage'));
const AvatarFramesPage = lazy(() => import('./pages/AvatarFramesPage/AvatarFramesPage'));
const AchievementsAdminPage = lazy(() => import('./pages/AchievementsAdminPage/AchievementsAdminPage'));
const PlayerApplicationsPage = lazy(() => import('./pages/PlayerApplicationsPage/PlayerApplicationsPage'));
const AfterSalesPage = lazy(() => import('./pages/AfterSalesPage/AfterSalesPage'));
const BossRightsAdminPage = lazy(() => import('./pages/BossRightsAdminPage/BossRightsAdminPage'));
const PlayerBackgroundsPage = lazy(() => import('./pages/PlayerBackgroundsPage/PlayerBackgroundsPage'));
// 微信扫码登录（独立全屏页）
const QrConfirmPage = lazy(() => import('./pages/connect/QrConfirmPage'));
const QrCallbackPage = lazy(() => import('./pages/connect/QrCallbackPage'));
// 新版陪玩端（移动端，参照老板端，独立域名）
const PlayLayout = lazy(() => import('./pages/play-portal/PlayLayout'));
const PlayHome = lazy(() => import('./pages/play-portal/PlayHome'));
const PlayOrders = lazy(() => import('./pages/play-portal/PlayOrders'));
const PlayRanking = lazy(() => import('./pages/play-portal/PlayRanking'));
const PlayMine = lazy(() => import('./pages/play-portal/PlayMine'));
const PlayReviews = lazy(() => import('./pages/play-portal/PlayReviews'));
const PlayOrderDetail = lazy(() => import('./pages/play-portal/PlayOrderDetail'));
const PlayQrConfirmPage = lazy(() => import('./pages/connect/PlayQrConfirmPage'));

// 加载时的兜底UI
const PageLoading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-gray-500">加载中...</div>
  </div>
);

// 登录后在浏览器空闲时预加载所有页面chunk，避免切换时白屏
const usePreloadPages = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  useEffect(() => {
    if (!hasHydrated || !isAuthenticated) return;
    const preloaders = [
      () => import('./pages/DashboardPage/DashboardPage'),
      () => import('./pages/OrdersPage/OrdersPage'),
      () => import('./pages/PlayersPage/PlayersPage'),
      () => import('./pages/BossesPage/BossesPage'),
      () => import('./pages/FinancePage/FinancePage'),
      () => import('./pages/ShopPage/ShopPage'),
      () => import('./pages/WithdrawalsPage/WithdrawalsPage'),
      () => import('./pages/UsersPage/UsersPage'),
      () => import('./pages/SettingsPage/SettingsPage'),
      () => import('./pages/InternalPage/InternalPage'),
      () => import('./pages/CouponsPage/CouponsPage'),
      () => import('./pages/LevelsPage'),
      () => import('./pages/GameItemsPage/GameItemsPage'),
      () => import('./pages/CustomerServicePage/CustomerServicePage'),
      () => import('./pages/MyOrdersPage/MyOrdersPage'),
      () => import('./pages/MyWithdrawalsPage/MyWithdrawalsPage'),
      () => import('./pages/PlayerAccountPage/PlayerAccountPage'),
      () => import('./pages/PlayerFundFlowPage/PlayerFundFlowPage'),
      () => import('./pages/PlayerRankingPage/PlayerRankingPage'),
    ];
    let cancelled = false;
    const ric: any = (window as any).requestIdleCallback || ((cb: any) => setTimeout(cb, 1));
    const run = (index: number) => {
      if (cancelled || index >= preloaders.length) return;
      ric(() => {
        try { preloaders[index](); } catch {}
        run(index + 1);
      });
    };
    const t = setTimeout(() => run(0), 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, [hasHydrated, isAuthenticated]);
};

const PrivateRoute = ({ children }: { children: React.ReactElement }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  // 等store从sessionStorage恢复完成后再判断，避免闪退回登录
  if (!hasHydrated) return null;
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const App = () => {
  usePreloadPages();
  return (
    <>
      <Toaster position="top-center" richColors />
      <Suspense
        fallback={
          <div className="flex min-h-[100dvh] items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#2AA3C4] border-t-transparent" />
          </div>
        }
      >
        <Routes>
        <Route path="/boss" element={<BossLayout />}>
          <Route index element={<Navigate to="/boss/home" replace />} />
          <Route path="home" element={<BossHome />} />
          <Route path="service" element={<BossService />} />
          <Route path="services" element={<Navigate to="/boss/service" replace />} />
          {/* 排行榜/陪玩列表已取消（入口与路由屏蔽，源码保留在 pages/boss-portal，后续可重新上线） */}
          <Route path="ranking" element={<Navigate to="/boss/home" replace />} />
          <Route path="players" element={<Navigate to="/boss/home" replace />} />
          <Route path="player/:id" element={<BossPlayerDetail />} />
          <Route path="mine" element={<BossMine />} />
          <Route path="orders" element={<BossOrders />} />
          <Route path="order/:orderId" element={<BossOrderDetail />} />
          <Route path="coupons" element={<BossCoupons />} />
          <Route path="achievements" element={<BossAchievements />} />
          <Route path="lottery" element={<BossLottery />} />
          <Route path="lottery-records" element={<BossLotteryRecords />} />
          <Route path="item/:id" element={<BossItemDetail />} />
          <Route path="aftersale/:orderId" element={<BossAfterSale />} />
          <Route path="rights" element={<BossRights />} />
        </Route>
        <Route path="/play" element={<PlayLayout />}>
          <Route index element={<Navigate to="/play/home" replace />} />
          <Route path="home" element={<PlayHome />} />
          <Route path="orders" element={<PlayOrders />} />
          <Route path="order/:orderId" element={<PlayOrderDetail />} />
          <Route path="reviews" element={<PlayReviews />} />
          <Route path="ranking" element={<PlayRanking />} />
          <Route path="mine" element={<PlayMine />} />
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/connect/wechat/qr-login/confirm" element={<QrConfirmPage />} />
        <Route path="/connect/wechat/qr-login/callback" element={<QrCallbackPage />} />
        <Route path="/play/connect/wechat/qr-login/confirm" element={<PlayQrConfirmPage />} />
        <Route path="/play/connect/wechat/qr-login/callback" element={<PlayQrConfirmPage />} />
        <Route path="/app/login" element={<Navigate to="/login" replace />} />
        <Route path="/" element={<Navigate to="/boss/" />} />
        <Route path="dashboard" element={<Navigate to="/app/dashboard" />} />
        <Route path="orders/*" element={<Navigate to="/app/orders" replace />} />
        <Route path="players/*" element={<Navigate to="/app/players" replace />} />
        <Route path="bosses/*" element={<Navigate to="/app/bosses" replace />} />
        <Route path="finance/*" element={<Navigate to="/app/finance" replace />} />
        <Route path="shop/*" element={<Navigate to="/app/shop" replace />} />
        <Route path="withdrawals/*" element={<Navigate to="/app/withdrawals" replace />} />
        <Route path="users/*" element={<Navigate to="/app/users" replace />} />
        <Route path="settings/*" element={<Navigate to="/app/settings" replace />} />
        <Route path="internal/*" element={<Navigate to="/app/internal" replace />} />
        <Route path="coupons" element={<Navigate to="/app/coupons" replace />} />
        <Route path="game-items" element={<Navigate to="/app/game-items" replace />} />
        <Route path="levels" element={<Navigate to="/app/levels" replace />} />
        <Route path="customer-service" element={<Navigate to="/app/customer-service" replace />} />
        <Route path="reviews" element={<Navigate to="/app/reviews" replace />} />
        <Route path="gifts" element={<Navigate to="/app/gifts" replace />} />
        <Route path="big-categories" element={<Navigate to="/app/big-categories" replace />} />
        <Route path="avatar-frames" element={<Navigate to="/app/avatar-frames" replace />} />
        <Route path="my-orders" element={<Navigate to="/app/my-orders" replace />} />
        <Route path="my-withdrawals" element={<Navigate to="/app/my-withdrawals" replace />} />
        <Route path="/app" element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index element={<Navigate to="/app/dashboard" />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="orders/*" element={<OrdersPage />} />
          <Route path="players/ranking" element={<PlayerRankingPage />} />
          <Route path="players/stats" element={<PlayerStatsPage />} />
          <Route path="players/account" element={<PlayerAccountPage />} />
          <Route path="players/fund-flow" element={<PlayerFundFlowPage />} />
          <Route path="players/*" element={<PlayersPage />} />
          <Route path="bosses/*" element={<BossesPage />} />
          <Route path="finance/*" element={<FinancePage />} />
          <Route path="shop/*" element={<ShopPage />} />
          <Route path="withdrawals/*" element={<WithdrawalsPage />} />
          <Route path="users/*" element={<UsersPage />} />
          <Route path="settings/*" element={<SettingsPage />} />
          <Route path="internal" element={<InternalPage />} />
          <Route path="coupons" element={<CouponsPage />} />
          <Route path="game-items" element={<GameItemsPage />} />
          <Route path="customer-service" element={<CustomerServicePage />} />
          <Route path="reviews" element={<ReviewsPage />} />
          <Route path="gifts" element={<GiftsPage />} />
          <Route path="big-categories" element={<BigCategoriesPage />} />
          <Route path="avatar-frames" element={<AvatarFramesPage />} />
          <Route path="levels" element={<LevelsPage />} />
          <Route path="achievements" element={<AchievementsAdminPage />} />
          <Route path="player-applications" element={<PlayerApplicationsPage />} />
          <Route path="after-sales" element={<AfterSalesPage />} />
          <Route path="boss-rights" element={<BossRightsAdminPage />} />
          <Route path="player-backgrounds" element={<PlayerBackgroundsPage />} />
          <Route path="my-orders" element={<MyOrdersPage />} />
          <Route path="my-withdrawals" element={<MyWithdrawalsPage />} />
        </Route>
      </Routes>
      </Suspense>
    </>
  );
};

export default App;
